import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowRight,
  BarChart3,
  Check,
  CircleDollarSign,
  Menu,
  Plug,
  Puzzle,
  ShieldCheck,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { BrandMark } from "@/components/layout/Sidebar";
import { cn } from "@/lib/utils";

/* ---------------- Purely presentational content ---------------- */

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#providers", label: "Providers" },
  { href: "#integration", label: "Integration" },
  { href: "#pricing", label: "Pricing" },
];

const PROVIDERS = ["OpenAI", "Anthropic", "Google", "Mistral", "Cohere", "DeepSeek", "xAI", "Azure OpenAI"];

const SOLUTIONS = [
  { title: "API Access", description: "Simple REST API access to multiple models with a unified interface.", icon: Plug },
  { title: "Cost Optimization", description: "Automatically route requests to the best-performing model for your budget.", icon: CircleDollarSign },
  { title: "Analytics", description: "Comprehensive metrics and insights for all your AI operations.", icon: BarChart3 },
  { title: "Fine-tuning", description: "Simplify the process of fine-tuning models with your custom data.", icon: Puzzle },
  { title: "Team Management", description: "Organize users, manage permissions, and control access to resources.", icon: Users },
  { title: "Enterprise Security", description: "Advanced security features for enterprise deployments.", icon: ShieldCheck },
];

/** Illustrative bars for the hero mock (not live data). */
const MOCK_BARS = [
  { a: 34, b: 22 },
  { a: 42, b: 28 },
  { a: 38, b: 24 },
  { a: 56, b: 30 },
  { a: 48, b: 34 },
  { a: 64, b: 38 },
  { a: 72, b: 44 },
];
const MOCK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MOCK_ROUTES = [
  { name: "OpenAI", share: 46, cls: "bg-brand" },
  { name: "Anthropic", share: 31, cls: "bg-chart-1" },
  { name: "Google", share: 15, cls: "bg-chart-4" },
  { name: "Other", share: 8, cls: "bg-chart-6" },
];

const PLANS = [
  { name: "Free", blurb: "Explore the playground and the unified API.", points: ["Unified /v1 endpoint", "Usage dashboard"] },
  { name: "Professional", blurb: "Usage-based pricing for production traffic.", points: ["Caching & fallback routing", "Budgets and usage alerts"], featured: true },
  { name: "Enterprise", blurb: "Custom limits, invoicing and support.", points: ["Team & role management", "Dedicated support"] },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
});

const navLinkClass = "text-sm text-foreground/75 hover:text-foreground transition-colors";
const footerLinkClass = "text-[13px] text-muted-foreground hover:text-foreground transition-colors";

function SectionHeading({ eyebrow, title, accent, description, className }: { eyebrow?: string; title: string; accent?: string; description?: string; className?: string }) {
  return (
    <div className={cn("max-w-2xl", className)}>
      {eyebrow && <p className="text-[13px] text-muted-foreground mb-3">{eyebrow}</p>}
      <h2 className="text-[30px] sm:text-[40px] font-medium tracking-[-0.03em] leading-[1.08]">
        {title}
        {accent && <span className="block text-subtle">{accent}</span>}
      </h2>
      {description && <p className="text-[15px] sm:text-base text-muted-foreground mt-4 leading-relaxed">{description}</p>}
    </div>
  );
}

/** Hero visual assembled from design-system pieces (illustrative). */
function HeroMock() {
  return (
    <div className="rounded-card bg-card p-2 sm:p-3">
      <div className="rounded-[16px] bg-surface-muted/70 p-2 grid gap-2 lg:grid-cols-[minmax(0,1fr)_260px]">
        {/* Usage chart tile */}
        <div className="rounded-[16px] bg-card p-5 sm:p-6 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[13px] text-muted-foreground">Tokens processed</p>
              <p className="text-[34px] sm:text-[44px] leading-none font-medium tracking-tight tabular mt-2">48.2M</p>
              <p className="inline-flex items-center gap-1 text-[13px] mt-2">
                <span className="inline-flex items-center gap-1 font-medium text-success">
                  <TrendingUp className="h-3.5 w-3.5" /> 12.4%
                </span>
                <span className="text-muted-foreground">from last week</span>
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-chart-1" />Completion</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-brand" />Prompt</span>
            </div>
          </div>
          <div className="mt-8 flex items-end gap-2 sm:gap-3 h-40" aria-hidden>
            {MOCK_BARS.map((bar, i) => (
              <div key={MOCK_DAYS[i]} className="flex-1 flex flex-col items-stretch gap-1.5 h-full justify-end">
                <span className="rounded-[8px] bg-chart-1" style={{ height: `${bar.b}%` }} />
                <span className={cn("rounded-[8px]", i === MOCK_BARS.length - 1 ? "bg-brand" : "bg-stripes-brand")} style={{ height: `${bar.a}%` }} />
                <span className="text-[11px] text-subtle text-center mt-1">{MOCK_DAYS[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Side tiles */}
        <div className="grid gap-2 grid-cols-2 lg:grid-cols-1">
          <div className="rounded-[16px] bg-card p-5">
            <p className="text-[13px] text-muted-foreground">Success rate</p>
            <p className="text-[28px] leading-none font-medium tracking-tight tabular mt-2">99.6%</p>
          </div>
          <div className="rounded-[16px] bg-card p-5">
            <p className="text-[13px] text-muted-foreground">Avg. latency</p>
            <p className="text-[28px] leading-none font-medium tracking-tight tabular mt-2">412ms</p>
          </div>
          <div className="rounded-[16px] bg-card p-5 col-span-2 lg:col-span-1">
            <p className="text-[13px] text-muted-foreground">Routed by provider</p>
            <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full gap-0.5" aria-hidden>
              {MOCK_ROUTES.map((r) => (
                <span key={r.name} className={cn("h-full rounded-full", r.cls)} style={{ width: `${r.share}%` }} />
              ))}
            </div>
            <ul className="mt-3 grid gap-1.5">
              {MOCK_ROUTES.map((r) => (
                <li key={r.name} className="flex items-center justify-between text-[13px]">
                  <span className="inline-flex items-center gap-2"><span className={cn("h-2 w-2 rounded-full", r.cls)} />{r.name}</span>
                  <span className="text-muted-foreground tabular">{r.share}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const LandingPage = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleDashboardClick = () => {
    navigate("/");
  };

  const handleGetStartedClick = () => {
    if (isAuthenticated) {
      navigate("/");
    } else {
      navigate("/login");
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-canvas text-foreground">
      {/* ---------------- Navigation ---------------- */}
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-colors duration-300",
          scrolled || mobileMenuOpen ? "bg-canvas/85 backdrop-blur-md border-b border-border/60" : "border-b border-transparent"
        )}
      >
        <div className="mx-auto max-w-6xl h-16 px-4 sm:px-6 flex items-center justify-between gap-6">
          <BrandMark to="/" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a key={l.href} className={navLinkClass} href={l.href}>
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            {isLoading ? (
              <div className="h-10 w-24 bg-surface-muted animate-pulse rounded-full" />
            ) : isAuthenticated ? (
              <>
                <span className="text-[13px] text-muted-foreground hidden lg:inline-block mr-1">
                  Welcome, <span className="text-foreground">{user?.username || 'User'}</span>
                </span>
                <Button variant="ghost" className="hidden sm:flex rounded-full h-10 px-4" onClick={handleDashboardClick}>
                  Dashboard
                </Button>
              </>
            ) : (
              <Button variant="ghost" className="hidden sm:flex rounded-full h-10 px-4" onClick={handleLoginClick}>
                Log in
              </Button>
            )}
            <Button className="rounded-full h-10 px-5" onClick={handleGetStartedClick}>
              {isAuthenticated ? "Go to Dashboard" : "Request a demo"}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full border border-border bg-card hover:bg-surface-muted transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" strokeWidth={1.8} /> : <Menu className="h-5 w-5" strokeWidth={1.8} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden px-4 pb-4">
            <div className="rounded-card bg-card p-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="block rounded-xl px-3 py-2.5 text-[15px] hover:bg-surface-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {l.label}
                </a>
              ))}

              <div className="pt-3 mt-2 border-t border-border/60 flex flex-col gap-2">
                {isAuthenticated ? (
                  <>
                    <span className="px-3 pt-1 text-[13px] text-muted-foreground">
                      Welcome, <span className="text-foreground">{user?.username || 'User'}</span>
                    </span>
                    <Button
                      variant="ghost"
                      className="justify-start rounded-xl h-11 px-3"
                      onClick={() => {
                        handleDashboardClick();
                        setMobileMenuOpen(false);
                      }}
                    >
                      Dashboard
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    className="justify-start rounded-xl h-11 px-3"
                    onClick={() => {
                      handleLoginClick();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Log in
                  </Button>
                )}
                <Button
                  className="rounded-full h-11 w-full"
                  onClick={() => {
                    handleGetStartedClick();
                    setMobileMenuOpen(false);
                  }}
                >
                  {isAuthenticated ? "Go to Dashboard" : "Request a demo"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* ---------------- Hero ---------------- */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-32 sm:pt-40 pb-16 sm:pb-24">
          <div className="max-w-4xl">
            <motion.p {...fadeUp(0)} className="inline-flex items-center gap-2 h-8 rounded-full border border-border bg-card px-3 text-xs font-medium text-foreground/80">
              <span className="h-2 w-2 rounded-full bg-brand" />
              The unified interface for all your AI models
            </motion.p>

            <motion.h1
              {...fadeUp(0.1)}
              className="mt-6 text-[44px] sm:text-[64px] lg:text-[76px] font-medium tracking-[-0.04em] leading-[0.98]"
            >
              One API for every model.
              <span className="block text-subtle">Route, observe and control 20+ LLM providers.</span>
            </motion.h1>

            <motion.p {...fadeUp(0.2)} className="mt-7 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              GenAICore provides a single API to access OpenAI, Anthropic, Gemini, and more—with built-in authentication, usage tracking, and smart request routing.
            </motion.p>

            <motion.div {...fadeUp(0.3)} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button size="lg" className="rounded-full h-12 pl-6 pr-5 text-sm gap-3" onClick={handleGetStartedClick}>
                {isAuthenticated ? "Go to Dashboard" : "Get started for free"}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-6 text-sm"
                onClick={() => window.location.href = "#features"}
              >
                View documentation
              </Button>
            </motion.div>
          </div>

          {/* Product showcase */}
          <motion.div
            className="mt-16 sm:mt-20"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
          >
            <HeroMock />
          </motion.div>
        </section>

        {/* ---------------- Provider strip ---------------- */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 sm:pb-24">
          <h2 className="text-[13px] text-muted-foreground mb-5">Integrated with leading AI providers</h2>
          <ul className="flex flex-wrap gap-2">
            {PROVIDERS.map((name) => (
              <li key={name} className="inline-flex items-center gap-2.5 h-11 rounded-full bg-card pl-1.5 pr-4 text-sm font-medium">
                <span className="h-8 w-8 rounded-full bg-surface-muted flex items-center justify-center text-[11px] font-semibold text-foreground/80" aria-hidden>
                  {name.slice(0, 2)}
                </span>
                {name}
              </li>
            ))}
          </ul>
        </section>

        {/* ---------------- Features ---------------- */}
        <section id="features" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <SectionHeading eyebrow="Platform" title="All your AI models" accent="through a single API" className="mb-12 sm:mb-16" />

          <div className="grid gap-4 lg:grid-cols-2 items-stretch">
            {/* Unified API */}
            <div className="rounded-card bg-card p-6 sm:p-8 flex flex-col justify-between gap-8">
              <div>
                <span className="h-10 w-10 rounded-xl bg-surface-muted flex items-center justify-center mb-6">
                  <Plug className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="text-[22px] font-medium tracking-tight">Unified API</h3>
                <p className="text-[15px] text-muted-foreground mt-3 leading-relaxed">
                  Access OpenAI, Anthropic, Google, Mistral, and more through a single, consistent API. No need to manage multiple SDKs and authentication flows.
                </p>
                <div className="mt-6 rounded-xl bg-surface-muted p-4 sm:p-5 font-fragment text-[13px] leading-relaxed overflow-auto">
                  <pre className="text-foreground">
                    <span className="inline-flex items-center rounded-md bg-brand-soft text-foreground px-1.5 mr-2">POST</span>
                    api.genaicore.com/api/completions
                  </pre>
                  <pre className="text-muted-foreground mt-3">{'{\n  "model": "gpt-4",\n  "provider": "openai",\n  "messages": [...]\n}'}</pre>
                </div>
              </div>
              <a href="#" className="group inline-flex w-fit items-center gap-2 text-sm font-medium hover:text-foreground/80">
                View API documentation
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>

            {/* Smart management */}
            <div className="rounded-card bg-card p-6 sm:p-8 flex flex-col justify-between gap-8">
              <div>
                <span className="h-10 w-10 rounded-xl bg-surface-muted flex items-center justify-center mb-6">
                  <BarChart3 className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="text-[22px] font-medium tracking-tight">Smart Management</h3>
                <p className="text-[15px] text-muted-foreground mt-3 leading-relaxed">
                  Monitor usage, control costs, and analyze performance across all your AI providers through a comprehensive dashboard.
                </p>
                <div className="mt-6 rounded-[16px] bg-surface-muted/70 p-2 grid gap-2">
                  <div className="rounded-xl bg-card p-4">
                    <h4 className="text-sm font-medium">Usage Analytics</h4>
                    <p className="text-[13px] text-muted-foreground mt-1">Real-time metrics on token usage and request volumes.</p>
                  </div>
                  <div className="rounded-xl bg-card p-4">
                    <div className="flex items-center justify-between gap-4">
                      <h4 className="text-sm font-medium">Cost Management</h4>
                      <span className="text-xs text-muted-foreground tabular">72% of budget</span>
                    </div>
                    <p className="text-[13px] text-muted-foreground mt-1">Set budgets and receive alerts when nearing limits.</p>
                    <div className="mt-3 h-2 rounded-full bg-surface-muted overflow-hidden" aria-hidden>
                      <span className="block h-full w-[72%] rounded-full bg-stripes-brand" />
                    </div>
                  </div>
                </div>
              </div>
              <a href="#" className="group inline-flex w-fit items-center gap-2 text-sm font-medium hover:text-foreground/80">
                Explore dashboard
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </section>

        {/* ---------------- Solutions grid ---------------- */}
        <section id="providers" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <SectionHeading
            eyebrow="Solutions"
            title="Solutions for every need"
            accent="From prototype to production."
            description="GenAICore offers flexible solutions to fit any project requirement, from startups to enterprise deployments."
            className="mb-12 sm:mb-16"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SOLUTIONS.map((solution) => {
              const Icon = solution.icon;
              return (
                <div key={solution.title} className="rounded-card bg-card p-6 transition-colors hover:bg-card/70">
                  <span className="h-10 w-10 rounded-xl bg-surface-muted flex items-center justify-center mb-8">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <h3 className="text-lg font-medium">{solution.title}</h3>
                  <p className="text-[15px] text-muted-foreground mt-2 leading-relaxed">{solution.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ---------------- Pricing teaser ---------------- */}
        <section id="pricing" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <SectionHeading eyebrow="Pricing" title="Start free." accent="Pay as you scale." className="mb-12 sm:mb-16" />
          <div className="grid gap-4 md:grid-cols-3">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={cn(
                  "rounded-card p-6 sm:p-7 flex flex-col",
                  plan.featured ? "bg-primary text-primary-foreground" : "bg-card"
                )}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">{plan.name}</h3>
                  {plan.featured && (
                    <span className="inline-flex items-center h-6 rounded-full bg-brand text-brand-foreground px-2.5 text-[11px] font-semibold">Recommended</span>
                  )}
                </div>
                <p className={cn("text-[15px] mt-2", plan.featured ? "text-primary-foreground/65" : "text-muted-foreground")}>{plan.blurb}</p>
                <ul className="mt-6 grid gap-2.5 flex-1">
                  {plan.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2.5 text-sm">
                      <span className={cn("h-5 w-5 rounded-full flex items-center justify-center", plan.featured ? "bg-brand text-brand-foreground" : "bg-surface-muted")}>
                        <Check className="h-3 w-3" strokeWidth={2.4} />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.featured ? "brand" : "outline"}
                  className="mt-8 rounded-full h-11"
                  onClick={handleGetStartedClick}
                >
                  {isAuthenticated ? "Go to Dashboard" : "Get started"}
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------- CTA ---------------- */}
        <section id="integration" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
          <div className="relative overflow-hidden rounded-card bg-card p-8 sm:p-12 md:p-16">
            <div className="absolute right-0 top-0 bottom-0 hidden md:flex items-end gap-2 p-8 w-[38%]" aria-hidden>
              {[30, 46, 38, 62, 54, 78, 92].map((h, i, arr) => (
                <span key={i} className={cn("flex-1 rounded-[10px]", i === arr.length - 1 ? "bg-brand" : i % 2 ? "bg-stripes-brand" : "bg-surface-muted")} style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="relative max-w-xl">
              <h2 className="text-[30px] sm:text-[40px] font-medium tracking-[-0.03em] leading-[1.08]">
                Ready to get started?
                <span className="block text-subtle">Ship on every model today.</span>
              </h2>
              <p className="text-[15px] sm:text-base text-muted-foreground mt-4">
                Join thousands of developers already using GenAICore to power their AI applications.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button size="lg" className="rounded-full h-12 pl-6 pr-5 text-sm gap-3" onClick={handleGetStartedClick}>
                  {isAuthenticated ? "Go to Dashboard" : "Get started for free"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full h-12 px-6 text-sm"
                  onClick={() => window.open('mailto:sales@genaicore.com')}
                >
                  Contact sales
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- Footer ---------------- */}
      <footer className="mx-auto max-w-6xl px-4 sm:px-6 pb-10 pt-6">
        <div className="rounded-card bg-card p-6 sm:p-10">
          <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-8">
            <div className="col-span-2 md:col-span-1">
              <BrandMark to="/" />
              <p className="text-[13px] text-muted-foreground mt-4 max-w-xs">One API for every model. Route, observe and control 20+ LLM providers.</p>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Platform</h3>
              <ul className="space-y-2.5">
                <li><a href="#features" className={footerLinkClass}>Features</a></li>
                <li><a href="#providers" className={footerLinkClass}>Providers</a></li>
                <li><a href="#integration" className={footerLinkClass}>Integration</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Documentation</h3>
              <ul className="space-y-2.5">
                <li><Link to="/docs" className={footerLinkClass}>Documentation Hub</Link></li>
                <li><Link to="/docs/platform-overview" className={footerLinkClass}>Platform Overview</Link></li>
                <li><Link to="/docs/get-started/introduction" className={footerLinkClass}>Getting Started</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-4">Legal</h3>
              <ul className="space-y-2.5">
                <li><a href="#" className={footerLinkClass}>Terms of Service</a></li>
                <li><a href="#" className={footerLinkClass}>Privacy Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/60 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground text-center md:text-left">
              &copy; {new Date().getFullYear()} GenAICore. All rights reserved.
            </p>
            <a href="#" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-surface-muted transition-colors">
              <span className="sr-only">GitHub</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
